"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Plus, Edit, Trash2, Users, Search, Phone, Mail } from "lucide-react"
import { useNavigate } from "react-router-dom"
import type { Fencer } from "../types"
import { api } from "../services/api"
import { FencerForm } from "../components/FencerForm"
import { calculateAge, isAdult } from "../utils/dateUtils"

export const FencersPage: React.FC = () => {
  const navigate = useNavigate()
  const [fencers, setFencers] = useState<Fencer[]>([])
  const [filteredFencers, setFilteredFencers] = useState<Fencer[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [showForm, setShowForm] = useState(false)
  const [editingFencer, setEditingFencer] = useState<Fencer | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadFencers()
  }, [])

  useEffect(() => {
    // Filter fencers based on search term
    const filtered = fencers.filter(
      (fencer) =>
        fencer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fencer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fencer.guardians.some((guardian) => guardian.name.toLowerCase().includes(searchTerm.toLowerCase())),
    )
    setFilteredFencers(filtered)
  }, [fencers, searchTerm])

  const loadFencers = async () => {
    try {
      const data = await api.getFencers()
      setFencers(data)
    } catch (error) {
      console.error("Error loading fencers:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddFencer = async (fencerData: Omit<Fencer, "id">) => {
    try {
      await api.addFencer(fencerData)
      await loadFencers()
      setShowForm(false)
    } catch (error) {
      console.error("Error adding fencer:", error)
    }
  }

  const handleUpdateFencer = async (fencerData: Omit<Fencer, "id">) => {
    if (!editingFencer) return

    try {
      await api.updateFencer(editingFencer.id, fencerData)
      await loadFencers()
      setEditingFencer(null)
      setShowForm(false)
    } catch (error) {
      console.error("Error updating fencer:", error)
    }
  }

  const handleDeleteFencer = async (id: string) => {
    if (!confirm("Are you sure you want to delete this fencer?")) return

    try {
      await api.deleteFencer(id)
      await loadFencers()
    } catch (error) {
      console.error("Error deleting fencer:", error)
    }
  }

  const handleEdit = (fencer: Fencer) => {
    setEditingFencer(fencer)
    setShowForm(true)
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingFencer(null)
  }

  const handleFencerClick = (fencerId: string) => {
    navigate(`/fencers/${fencerId}`)
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading fencers...</span>
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Fencers</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">Manage your fencing club members</p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:shadow-md"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Fencer
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mt-6 card p-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          </div>
          <input
            type="text"
            placeholder="Search fencers by name, email, or parent name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-500 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
          />
        </div>
        {searchTerm && (
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Found {filteredFencers.length} fencer{filteredFencers.length !== 1 ? "s" : ""} matching "{searchTerm}"
          </div>
        )}
      </div>

      {showForm && (
        <div className="mt-8 card p-6 animate-slide-down">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {editingFencer ? "Edit Fencer" : "Add New Fencer"}
          </h2>
          <FencerForm
            onSubmit={editingFencer ? handleUpdateFencer : handleAddFencer}
            onCancel={handleCancel}
            initialData={editingFencer || undefined}
          />
        </div>
      )}

      <div className="mt-8 card overflow-hidden animate-slide-up">
        {/* Desktop Table */}
        <div className="hidden md:block">
          <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                  Fencer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                  Age
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                  Parent/Guardian
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                  Registration Date
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredFencers.map((fencer) => {
                const age = calculateAge(fencer.dateOfBirth)
                const adult = isAdult(fencer.dateOfBirth)

                return (
                  <tr
                    key={fencer.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 cursor-pointer"
                    onClick={() => handleFencerClick(fencer.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{fencer.name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-300">{fencer.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{age} years</div>
                      <div
                        className={`text-xs ${adult ? "text-green-600 dark:text-green-400" : "text-blue-600 dark:text-blue-400"}`}
                      >
                        {adult ? "Adult" : "Minor"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {fencer.phone}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {fencer.guardians.length > 0 ? (
                        <div>
                          <div>{fencer.guardians[0].name}</div>
                          <div className="text-xs">{fencer.guardians[0].phone}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 italic">N/A (Adult)</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {fencer.registrationDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEdit(fencer)
                        }}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-3 p-1 rounded transition-colors duration-200"
                        title="Edit fencer"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteFencer(fencer.id)
                        }}
                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded transition-colors duration-200"
                        title="Delete fencer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Card Layout */}
        <div className="md:hidden">
          {filteredFencers.map((fencer) => {
            const age = calculateAge(fencer.dateOfBirth)
            const adult = isAdult(fencer.dateOfBirth)

            return (
              <div
                key={fencer.id}
                className="p-4 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                onClick={() => handleFencerClick(fencer.id)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{fencer.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {age} years • {adult ? "Adult" : "Minor"}
                    </div>
                    <div className="flex items-center mt-2 space-x-4">
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <Mail className="h-3 w-3 mr-1" />
                        <span className="truncate max-w-32">{fencer.email}</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <Phone className="h-3 w-3 mr-1" />
                        <span>{fencer.phone}</span>
                      </div>
                    </div>
                    {!adult && fencer.guardians.length > 0 && (
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        Parent: {fencer.guardians[0].name}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 ml-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleEdit(fencer)
                      }}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1 rounded transition-colors duration-200"
                      title="Edit fencer"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteFencer(fencer.id)
                      }}
                      className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded transition-colors duration-200"
                      title="Delete fencer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {filteredFencers.length === 0 && !searchTerm && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No fencers</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by adding your first fencer.</p>
          </div>
        )}
        {filteredFencers.length === 0 && searchTerm && (
          <div className="text-center py-12">
            <Search className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No results found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Try adjusting your search terms or add a new fencer.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
